import { RotateCcw } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/shared/components/ui/button';
import { env } from '@/shared/lib/env';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered instead of the default screen; receives a reset callback. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Last line of defence against a render-time crash taking the whole app down.
 *
 * Data-fetching failures are handled by `QueryBoundary`; this catches the
 * genuinely unexpected — a bad render, a null dereference — and keeps the user
 * on a screen they can recover from. Must stay a class component: React
 * exposes no hook equivalent of `componentDidCatch`.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Wire an error reporter (Sentry et al.) here; the console keeps local
    // development debuggable in the meantime.
    if (env.isDev) console.error('Unhandled render error:', error, info.componentStack);
  }

  private readonly reset = (): void => this.setState({ error: null });

  override render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-lg font-bold">برنامه با خطا مواجه شد</h1>
        <p className="text-muted-foreground max-w-xs text-sm leading-relaxed text-balance">
          مشکلی غیرمنتظره رخ داد. با بازگشت به صفحه اصلی می‌توانید ادامه دهید.
        </p>
        {env.isDev ? (
          <pre
            className="bg-muted text-muted-foreground max-w-full overflow-auto rounded-lg p-3 text-start text-xs"
            dir="ltr"
          >
            {error.message}
          </pre>
        ) : null}
        <div className="flex gap-2">
          <Button onClick={this.reset}>
            <RotateCcw className="size-4" />
            تلاش دوباره
          </Button>
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            صفحه اصلی
          </Button>
        </div>
      </div>
    );
  }
}
